import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Heading,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4}>
        <Card maxW="lg" mx="auto" w="full">
          <CardBody>
            <VStack spacing={4} textAlign="center">
              <Icon as={AlertTriangle} boxSize={16} color="red.400" />
              <Heading size="lg" fontFamily="heading">Something went wrong</Heading>
              <Text color="stone.500">
                An unexpected error occurred. Please try refreshing the page.
              </Text>
              {isLocalhost && this.state.error && (
                <Box w="full" textAlign="left">
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    p={4}
                    borderRadius="md"
                    fontSize="sm"
                    colorScheme="red"
                    w="full"
                  >
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </Code>
                </Box>
              )}
              <Button
                leftIcon={<RotateCcw size={16} />}
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    )
  }
}
